import _ from 'lodash';
import {calculateGrossPrice, calculatePrice} from './productUtils';

export const generateBreadcrumbJsonLd = (breadCrumbList = []) => {

    if (!Array.isArray(breadCrumbList) || breadCrumbList.length === 0)
        return;

    const itemListElement = breadCrumbList.map((element, index) => {

        const {text, url} = element;

        return {
            '@type': 'ListItem',
            'position': index + 1,
            'name': text,
            'item': url,
        };

    });

    return {
        '@context': 'https://schema.org',
        '@type': 'BreadcrumbList',
        'itemListElement': itemListElement,
    };
};

/**
 * https://developers.google.com/search/docs/advanced/structured-data/video#structured-data-type-definitions
 * @param video
 */
export const generateVideoJsonLd = (video) => {

    const {
        title,
        description,
        thumbnailUrl,
        contentUrl, // The url where this video is stored
        embedUrl, // The url where this video is shown
        uploadDateString,
    } = video;

    return {
        '@context': 'https://schema.org',
        '@type': 'VideoObject',
        'name': title,
        'description': description,
        'thumbnailUrl': thumbnailUrl,
        // 'uploadDate': '2016-03-31T08:00:00+08:00',
        'uploadDate': uploadDateString,
        // 'duration': 'PT1M54S',
        'contentUrl': contentUrl,
        'embedUrl': embedUrl,
    };

};

export const generateFaqJsonLd = (faqList = []) => {

    const mainEntity = faqList.map((item) => {

        const {content, title} = item;

        return {
            '@type': 'Question',
            'name': title,
            'acceptedAnswer': {
                '@type': 'Answer',
                'text': content,
            },
        };

    });

    return {
        '@context': 'https://schema.org',
        '@type': 'FAQPage',
        'mainEntity': mainEntity,
    };

};

export const generateProductJsonLd = (macroProduct, router, siteUrl) => {

    const {asPath} = router;
    const productUrl = `${siteUrl}${asPath}`;

    const {
        productDescription,
        reviews,
        title,
        sellingPointsTitle,
        microProducts,
        media: {
            wireframe: {
                url: mediaUrl,
            },
        },
        configLessProduct,
        SEO: {
            thumbnail,
            description,
        },
    } = macroProduct;
    const [firstMicroProduct] = microProducts;
    const {
        sku,
    } = firstMicroProduct;

    let installationPrice;
    let priceRange;

    if (configLessProduct) {

        const {installationService} = firstMicroProduct;
        installationPrice = installationService.price;

        const {
            netPrice: microProductNetPrice,
            tax: {taxPercent},
        } = firstMicroProduct.price;

        const {netPrice: installationNetPrice} = installationPrice;

        const microProductGrossPrice = calculateGrossPrice(microProductNetPrice,
            taxPercent);
        const installationGrossPrice = calculateGrossPrice(installationNetPrice,
            taxPercent);

        priceRange = [
            // microProductGrossPrice,
            microProductGrossPrice + installationGrossPrice];

    } else {

        installationPrice = macroProduct.extra.filter(extra => {

            // Find answer entitys in which you can find an installation price
            const isQuestion = extra.__typename.toLowerCase().includes('question');
            const isInstallationQuestion = !!extra.type &&
                extra.type.toLowerCase() ===
                'installation';
            return isQuestion && isInstallationQuestion;

        }).map(extra => {

            const installationAnswer = extra.Answer.find(item => {
                return !!item.installationCost;
            });

            return installationAnswer.installationCost.price;

        });
        const [installationPriceSample] = installationPrice;

        priceRange = _.uniq(_.flattenDeep(microProducts.map(product => {

            const {
                netPrice: microProductNetPrice,
                tax: {taxPercent},
            } = product.price;
            const {netPrice: installationNetPrice} = installationPriceSample;

            const microProductGrossPrice = calculateGrossPrice(microProductNetPrice,
                taxPercent);
            const installationGrossPrice = calculateGrossPrice(installationNetPrice,
                taxPercent);

            return [
                microProductGrossPrice,
                microProductGrossPrice + installationGrossPrice];

        }))).sort((a, b) => a >= b ? 1 : -1);
    }

    const aggregateRating = _.meanBy(reviews, (r) => r.stars).toFixed(2);
    const reviewCount = reviews.length;
    const bestRating = reviews.find(({stars}) => stars === 5);

    const offerCount = microProducts.length * 2;

    const deliveryTime = microProducts.map(product => {

        const {stocks} = product;
        const primaryStock = stocks.find(({primary}) => primary);
        const {minDay, maxDay} = primaryStock.shippingTime;

        return {
            minDay,
            maxDay,
        };

    }).sort((a, b) => a.maxDay >= b.maxDay ? 1 : -1);

    const jsonLd = {
        '@context': 'https://schema.org/',
        '@type': 'Product',
        'sku': sku,
        'image': [
            mediaUrl,
        ],
        'name': title,
        'description': sellingPointsTitle,
        'brand': {
            '@type': 'Brand',
            'name': 'AMPTech',
        },
        'offers': {
            '@type': 'AggregateOffer',
            'url': productUrl,
            'itemCondition': 'https://schema.org/NewCondition',
            'availability': 'https://schema.org/InStock',
            'lowPrice': priceRange[0].toFixed(2),
            'highPrice': priceRange[priceRange.length - 1].toFixed(2),
            'offerCount': offerCount.toString(),
            'priceCurrency': 'EUR',
            'shippingDetails': {
                '@type': 'OfferShippingDetails',
                'shippingRate': {
                    '@type': 'MonetaryAmount',
                    'value': '0',
                    'currency': 'EUR',
                },
                'shippingDestination': {
                    '@type': 'DefinedRegion',
                    'addressCountry': 'DE',
                },
                'deliveryTime': {
                    '@type': 'ShippingDeliveryTime',
                    'handlingTime': {
                        '@type': 'QuantitativeValue',
                        'minValue': deliveryTime[0].minDay,
                        'maxValue': deliveryTime[deliveryTime.length - 1].maxDay,
                    },
                    'cutOffTime': '17:00-09:00',
                    'businessDays': {
                        '@type': 'OpeningHoursSpecification',
                        'dayOfWeek': [
                            'https://schema.org/Monday',
                            'https://schema.org/Tuesday',
                            'https://schema.org/Wednesday',
                            'https://schema.org/Thursday',
                            'https://schema.org/Friday'],
                    },
                },
            },
        },
    };

    if (reviews.length > 0) {
        jsonLd['aggregateRating'] = {
            '@type': 'AggregateRating',
            'ratingValue': parseFloat(aggregateRating),
            'reviewCount': reviewCount,
        };

        jsonLd['review'] = {
            '@type': 'Review',
            'reviewRating': {
                '@type': 'Rating',
                'ratingValue': bestRating.stars,
                'bestRating': 5,
            },
            'author': {
                '@type': 'Person',
                'name': bestRating.authorName,
            },
        };
    }

    return jsonLd;
};
export const generateProductJsonLdFromVariationProduct = (
    variationProduct, router, siteUrl) => {

    const {asPath, locale} = router;
    const productUrl = `${siteUrl}/${locale}${asPath}`;

    const {
        reviews,
        shortTitle,
        simpleProducts,
        simpleProductInformation,
    } = variationProduct;

    const {
        mainImage: {url: mediaUrl},
        plainShortDescription,
    } = simpleProductInformation;

    const [firstSimpleProduct] = simpleProducts;
    const {
        sku,
        price,
    } = firstSimpleProduct;

    const {grossPrice} = calculatePrice(price);

    const aggregateRating = _.meanBy(reviews, (r) => r.stars).toFixed(2);
    const reviewCount = reviews.length;
    const bestRating = reviews.find(({stars}) => stars === 5);

    const deliveryTime = simpleProducts.map(product => {

        const {stocks} = product;
        const primaryStock = stocks.find(({primary}) => primary);

        if (!primaryStock)
            return {
                minDay: 14,
                maxDay: 116
            }

        const {minDay, maxDay} = primaryStock.shippingTime;

        return {
            minDay,
            maxDay,
        };

    }).sort((a, b) => a.maxDay >= b.maxDay ? 1 : -1);

    const jsonLd = {
        '@context': 'https://schema.org/',
        '@type': 'Product',
        'sku': sku,
        'image': [
            mediaUrl,
        ],
        'name': shortTitle,
        'description': plainShortDescription,
        'brand': {
            '@type': 'Brand',
            'name': 'AMPTech',
        },
        'offers': {
            '@type': 'Offer',
            'url': productUrl,
            'itemCondition': 'https://schema.org/NewCondition',
            'availability': 'https://schema.org/InStock',
            'price': grossPrice.toFixed(2),
            'priceCurrency': 'EUR',
            'shippingDetails': {
                '@type': 'OfferShippingDetails',
                'shippingRate': {
                    '@type': 'MonetaryAmount',
                    'value': '0',
                    'currency': 'EUR',
                },
                'shippingDestination': {
                    '@type': 'DefinedRegion',
                    'addressCountry': 'DE',
                },
                'deliveryTime': {
                    '@type': 'ShippingDeliveryTime',
                    'handlingTime': {
                        '@type': 'QuantitativeValue',
                        'minValue': deliveryTime[0].minDay,
                        'maxValue': deliveryTime[deliveryTime.length - 1].maxDay,
                    },
                    'cutOffTime': '17:00-09:00',
                    'businessDays': {
                        '@type': 'OpeningHoursSpecification',
                        'dayOfWeek': [
                            'https://schema.org/Monday',
                            'https://schema.org/Tuesday',
                            'https://schema.org/Wednesday',
                            'https://schema.org/Thursday',
                            'https://schema.org/Friday'],
                    },
                },
            },
        },
    };

    if (reviews.length > 0) {
        jsonLd['aggregateRating'] = {
            '@type': 'AggregateRating',
            'ratingValue': parseFloat(aggregateRating),
            'reviewCount': reviewCount,
        };

        jsonLd['review'] = {
            '@type': 'Review',
            'reviewRating': {
                '@type': 'Rating',
                'ratingValue': bestRating.stars,
                'bestRating': 5,
            },
            'author': {
                '@type': 'Person',
                'name': bestRating.authorName,
            },
        };
    }

    return jsonLd;
};
